import { useEffect, useState, useCallback } from "react";
import api, { errMsg, formatNumber } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import KPICard from "@/components/app/KPICard";
import ExportButtons from "@/components/app/ExportButtons";
import { outcomeMeta } from "@/components/app/TenderMail";
import { toast } from "sonner";
import { Send, Reply, ThumbsUp, ThumbsDown, Clock, ChevronDown, Search, Eye } from "lucide-react";

const TR_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const monthTitle = (m) => (m ? `${TR_MONTHS[parseInt(m.slice(5, 7), 10) - 1]} ${m.slice(0, 4)}` : "Tüm Aylar");
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "-");

export default function MailReportPage() {
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null); // genişletilen firma
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/mail-report", { params: month ? { month } : {} });
      setData(data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-8 text-sm text-neutral-500">Yükleniyor...</div>;
  const s = data.stats;
  const q = search.trim().toLowerCase();
  const firms = data.firms.filter(f => !q || `${f.name} ${f.email} ${f.contact_person}`.toLowerCase().includes(q));

  const exportSections = [
    {
      name: "Firmalar", heading: `Firma Bazlı Mail Özeti — ${monthTitle(month)}`,
      columns: [
        { key: "name", label: "Firma" },
        { key: "email", label: "Mail" },
        { key: "count", label: "Gönderim", format: "number" },
        { key: "opened", label: "Açılan", format: "number" },
        { key: "replied", label: "Yanıt Geldi", format: "number" },
        { key: "positive", label: "Olumlu", format: "number" },
        { key: "negative", label: "Olumsuz", format: "number" },
        { key: "pending", label: "Dönüş Yok", format: "number" },
        { key: "last", label: "Son Gönderim" },
      ],
      rows: [
        ...firms.map(f => ({ ...f, last: fmtDT(f.last_sent) })),
        { name: "TOPLAM", email: "", count: s.sent, opened: s.opened, replied: s.replied, positive: s.positive, negative: s.negative, pending: s.pending, last: "" },
      ],
    },
    {
      name: "Aylar", heading: "Ay Bazlı Özet",
      columns: [
        { key: "ay", label: "Ay" },
        { key: "sent", label: "Gönderim", format: "number" },
        { key: "opened", label: "Açılan", format: "number" },
        { key: "replied", label: "Yanıt Geldi", format: "number" },
        { key: "positive", label: "Olumlu", format: "number" },
        { key: "negative", label: "Olumsuz", format: "number" },
        { key: "pending", label: "Dönüş Yok", format: "number" },
      ],
      rows: data.by_month.map(m => ({ ...m, ay: monthTitle(m.month) })),
    },
  ];

  return (
    <>
      <PageHeader
        title="Mail Raporu"
        subtitle="Gönderilen maillerin ve geri dönüşlerin firma/ay bazlı özeti."
        testid="mail-report-header"
        actions={
          <div className="flex items-center gap-2">
            <select data-testid="mr-month" value={month} onChange={e => setMonth(e.target.value)} className="px-2.5 py-2 bg-white border border-[#E5E5E5] text-sm">
              <option value="">Tüm Aylar</option>
              {data.months.map(m => <option key={m} value={m}>{monthTitle(m)}</option>)}
            </select>
            <ExportButtons
              testidPrefix="mr-export"
              filename={`mail_raporu${month ? `_${month}` : ""}`}
              title={`MAİL RAPORU — ${monthTitle(month)}`}
              subtitle={`${s.sent} gönderim · ${s.replied} yanıt · ${s.positive} olumlu · ${s.negative} olumsuz · ${s.pending} dönüş bekliyor`}
              sections={exportSections}
            />
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <KPICard testid="mr-kpi-sent" label="Toplam Gönderim" value={formatNumber(s.sent)} sub={monthTitle(month)} accent="info" icon={Send} />
        <KPICard testid="mr-kpi-opened" label="Açılan" value={formatNumber(s.opened || 0)} sub={s.sent ? `%${Math.round((s.opened || 0) / s.sent * 100)} açılma` : ""} accent="success" icon={Eye} />
        <KPICard testid="mr-kpi-replied" label="Yanıt Geldi" value={formatNumber(s.replied)} sub={s.sent ? `%${Math.round(s.replied / s.sent * 100)} dönüş` : ""} accent="info" icon={Reply} />
        <KPICard testid="mr-kpi-positive" label="Olumlu" value={formatNumber(s.positive)} accent="success" icon={ThumbsUp} />
        <KPICard testid="mr-kpi-negative" label="Olumsuz" value={formatNumber(s.negative)} accent="error" icon={ThumbsDown} />
        <KPICard testid="mr-kpi-pending" label="Dönüş Bekleyen" value={formatNumber(s.pending)} sub="Takip edilmeli" accent="warning" icon={Clock} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Ay bazlı özet */}
        <div className="bg-white border border-[#E5E5E5] rounded-md lg:col-span-2" data-testid="mr-by-month">
          <div className="px-4 py-3 border-b border-[#E5E5E5] text-[11px] uppercase tracking-widest font-bold">Ay Bazlı Özet</div>
          <table className="w-full text-xs">
            <thead className="bg-[#FAFAFA]">
              <tr>{["Ay", "Gönderim", "Açılan", "Yanıt", "Olumlu", "Olumsuz", "Dönüş Yok"].map((h, i) => (
                <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i ? "text-right" : "text-left"}`}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {data.by_month.map(m => (
                <tr key={m.month} className={`border-t border-[#F5F5F5] ${month === m.month ? "bg-[#F5F8FF]" : ""} cursor-pointer hover:bg-[#FAFAFA]`} onClick={() => setMonth(month === m.month ? "" : m.month)}>
                  <td className="px-3 py-2 font-semibold">{monthTitle(m.month)}</td>
                  <td className="px-3 py-2 text-right num font-bold">{m.sent}</td>
                  <td className="px-3 py-2 text-right num text-[#16A34A]">{m.opened || 0}</td>
                  <td className="px-3 py-2 text-right num text-[#0020C2]">{m.replied}</td>
                  <td className="px-3 py-2 text-right num text-[#16A34A]">{m.positive}</td>
                  <td className="px-3 py-2 text-right num text-[#DC2626]">{m.negative}</td>
                  <td className="px-3 py-2 text-right num text-[#D97706]">{m.pending}</td>
                </tr>
              ))}
              {data.by_month.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-neutral-400">Henüz mail gönderilmemiş.</td></tr>}
            </tbody>
          </table>
        </div>
        {/* Taslak bazlı */}
        <div className="bg-white border border-[#E5E5E5] rounded-md" data-testid="mr-by-template">
          <div className="px-4 py-3 border-b border-[#E5E5E5] text-[11px] uppercase tracking-widest font-bold">Taslak Bazlı</div>
          <div className="p-4 space-y-2">
            {data.by_template.map(t => (
              <div key={t.template_name} className="flex items-center justify-between text-xs">
                <span className="truncate font-semibold">{t.template_name}</span>
                <span className="num font-bold text-[#0020C2]">{t.count}</span>
              </div>
            ))}
            {data.by_template.length === 0 && <div className="text-xs text-neutral-400">-</div>}
          </div>
        </div>
      </div>

      {/* Firma bazlı */}
      <div className="bg-white border border-[#E5E5E5] rounded-md" data-testid="mr-firms">
        <div className="px-4 py-3 border-b border-[#E5E5E5] flex items-center justify-between flex-wrap gap-2">
          <div className="text-[11px] uppercase tracking-widest font-bold">Firma Bazlı ({firms.length})</div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input data-testid="mr-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Firma ara..." className="pl-8 pr-3 py-1.5 border border-[#E5E5E5] text-xs w-56" />
          </div>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-[#FAFAFA]">
            <tr>{["Firma", "Son Gönderim", "Gönderim", "Açılan", "Yanıt", "Olumlu", "Olumsuz", "Dönüş Yok", ""].map((h, i) => (
              <th key={i} className={`px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 2 && i <= 7 ? "text-right" : "text-left"}`}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {firms.map(f => (
              <>
                <tr key={f.id} className="border-t border-[#F5F5F5] cursor-pointer hover:bg-[#FAFAFA]" data-testid={`mr-firm-${f.id}`} onClick={() => setOpen(open === f.id ? null : f.id)}>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{f.name}</div>
                    <div className="text-neutral-400">{f.email}</div>
                  </td>
                  <td className="px-3 py-2 num">{fmtDT(f.last_sent)}</td>
                  <td className="px-3 py-2 text-right num font-bold">{f.count}</td>
                  <td className="px-3 py-2 text-right num text-[#16A34A]">{f.opened || ""}</td>
                  <td className="px-3 py-2 text-right num text-[#0020C2]">{f.replied || ""}</td>
                  <td className="px-3 py-2 text-right num text-[#16A34A]">{f.positive || ""}</td>
                  <td className="px-3 py-2 text-right num text-[#DC2626]">{f.negative || ""}</td>
                  <td className="px-3 py-2 text-right num text-[#D97706]">{f.pending || ""}</td>
                  <td className="px-2 py-2"><ChevronDown className={`w-3.5 h-3.5 transition-transform ${open === f.id ? "rotate-180" : ""}`} /></td>
                </tr>
                {open === f.id && (
                  <tr className="bg-[#FAFAFA]">
                    <td colSpan={9} className="px-4 py-2">
                      <div className="space-y-1">
                        {f.mails.map(m => {
                          const [, label, cls] = outcomeMeta(m.outcome);
                          return (
                            <div key={m.id} className="flex items-center gap-2 text-xs bg-white border border-[#F5F5F5] px-2.5 py-1.5">
                              <span className="num font-bold whitespace-nowrap">{fmtDT(m.sent_at)}</span>
                              <span className="flex-1 truncate">{m.subject} <span className="text-neutral-400">· {m.template_name}</span></span>
                              {m.opened_at && <span className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-[#16A34A]/10 text-[#16A34A]" title={`İlk açılma: ${fmtDT(m.opened_at)}`}><Eye className="w-3 h-3" /> Açıldı</span>}
                              {m.outcome_note && <span className="italic text-neutral-500 truncate max-w-[200px]">"{m.outcome_note}"</span>}
                              <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest ${cls}`}>{label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
            {firms.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-neutral-400">Kayıt yok — İhale Takip ekranından mail gönderdikçe burada görünür.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}
